import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { ArrowLeft, Plus, Trash2, Check, MapPin, Sparkles } from "lucide-react";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));
const emptyCountry = () => ({ key: uid(), name: "", city: "", arrive_date: "", depart_date: "", highlight: "" });

// "Where next?" — creates a PLANNING journey (hidden from partners) starting from the current one.
export default function NewTrip() {
  const { config, org } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [trip, setTrip] = useState({ trip_name: "", start_date: "", end_date: "", currency: config?.currency || "USD", budget_goal: "",
    origin_country: config?.origin_country || "", origin_city: config?.origin_city || "" });
  const [copy, setCopy] = useState({ copy_team: true, copy_budget_items: true, copy_countries: false, copy_documents: false });
  const [countries, setCountries] = useState([emptyCountry()]);
  const setC = (key, k, v) => setCountries((cs) => cs.map((c) => (c.key === key ? { ...c, [k]: v } : c)));
  const validCountries = countries.filter((c) => c.name.trim());
  const fromName = config?.trip_name || "your last journey";

  const create = async () => {
    if (!trip.trip_name.trim()) { setStep(1); return toast.error("Give the journey a name first."); }
    setSaving(true);
    try {
      const { data } = await api.post("/trips", {
        trip_name: trip.trip_name.trim(), start_date: trip.start_date || null, end_date: trip.end_date || null,
        currency: trip.currency || "USD", origin_country: trip.origin_country || null, origin_city: trip.origin_city || null,
        budget_goal_cents: trip.budget_goal ? Math.round(parseFloat(trip.budget_goal) * 100) : 0,
        countries: copy.copy_countries ? [] : validCountries.map((c) => ({ name: c.name, city: c.city || null, arrive_date: c.arrive_date || null, depart_date: c.depart_date || null, highlight: c.highlight || null })),
        ...copy,
      });
      toast.success("Journey saved as Planning — partners won't see it until you make it current.");
      navigate(`/app/trips/${data.id}`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen -mx-4 -mt-4 px-5 pb-10 pt-6" style={{ background: "linear-gradient(180deg,#0d2340 0%,#0A1B33 100%)" }} data-testid="new-trip-page">
      <button data-testid="nt-back" onClick={() => navigate("/app/trips")} className="flex items-center gap-1 text-sm text-white/80 mb-5"><ArrowLeft className="h-4 w-4" /> Journeys</button>
      <div className="flex gap-2 mb-6">{[1, 2, 3].map((s) => <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? "bg-[#E0A73A]" : "bg-white/20"}`} />)}</div>
      <div className="text-[#E0A73A] text-xs font-semibold tracking-[0.2em] uppercase">Step {step} of 3 · {["Basics", "Countries", "Review"][step - 1]}</div>
      <h1 className="font-heading font-extrabold text-3xl text-white mt-2 leading-tight">{["Where next?", "Where are you going?", "Ready to plan"][step - 1]}</h1>

      <div className="mt-6 rounded-[28px] p-5 text-white" style={{ background: "rgba(10,27,51,.6)", border: "1px solid rgba(224,167,58,.3)", backdropFilter: "blur(12px)" }}>
        {step === 1 && (
          <div className="space-y-4">
            <F label="Journey name" testid="nt-name" value={trip.trip_name} onChange={(e) => setTrip({ ...trip, trip_name: e.target.value })} placeholder={`${org?.org_name || "Mission"} 2027`} />
            <div className="grid grid-cols-2 gap-3">
              <F label="Departs" testid="nt-start" type="date" value={trip.start_date} onChange={(e) => setTrip({ ...trip, start_date: e.target.value })} />
              <F label="Returns" testid="nt-end" type="date" value={trip.end_date} onChange={(e) => setTrip({ ...trip, end_date: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F label="Currency" testid="nt-currency" value={trip.currency} onChange={(e) => setTrip({ ...trip, currency: e.target.value })} />
              <F label="Budget goal" testid="nt-goal" type="number" value={trip.budget_goal} onChange={(e) => setTrip({ ...trip, budget_goal: e.target.value })} placeholder="24000" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F label="Home country" testid="nt-origin-country" value={trip.origin_country} onChange={(e) => setTrip({ ...trip, origin_country: e.target.value })} />
              <F label="Home city" testid="nt-origin-city" value={trip.origin_city} onChange={(e) => setTrip({ ...trip, origin_city: e.target.value })} />
            </div>
            <div className="pt-2">
              <div className="text-xs text-[#E0A73A] font-semibold uppercase tracking-[0.2em] flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Start from {fromName}</div>
              <div className="mt-3 space-y-3 text-sm">
                <Toggle testid="nt-copy-team" label="Team members" hint="Same roster is on this journey" checked={copy.copy_team} onChange={(v) => setCopy({ ...copy, copy_team: v })} />
                <Toggle testid="nt-copy-budget" label="Budget categories" hint="Categories only — amounts start at zero" checked={copy.copy_budget_items} onChange={(v) => setCopy({ ...copy, copy_budget_items: v })} />
                <Toggle testid="nt-copy-countries" label="Countries & itinerary" hint="Reuse the same route" checked={copy.copy_countries} onChange={(v) => setCopy({ ...copy, copy_countries: v })} />
                <Toggle testid="nt-copy-docs" label="Trip documents" hint="Packing lists, forms, guides" checked={copy.copy_documents} onChange={(v) => setCopy({ ...copy, copy_documents: v })} />
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            {copy.copy_countries ? (
              <p className="text-sm text-white/80">You chose to reuse the route from <b>{fromName}</b>. You can edit stops later from the Trip page.</p>
            ) : (
              <>
                <p className="text-sm text-white/70">Add each stop in the order you'll visit.</p>
                {countries.map((c, i) => (
                  <div key={c.key} className="rounded-2xl p-3 space-y-2" style={{ background: "rgba(255,255,255,.06)" }} data-testid="nt-country-row">
                    <div className="flex items-center gap-2">
                      <span className="h-7 w-7 rounded-full bg-[#E0A73A] text-[#0A1B33] text-sm font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                      <Input data-testid={`nt-country-name-${i}`} value={c.name} onChange={(e) => setC(c.key, "name", e.target.value)} placeholder="Country" className="rounded-xl h-10 bg-[#0A1B33]/50 border-white/20 text-white placeholder:text-white/40" />
                      <Input data-testid={`nt-country-city-${i}`} value={c.city} onChange={(e) => setC(c.key, "city", e.target.value)} placeholder="City" className="rounded-xl h-10 bg-[#0A1B33]/50 border-white/20 text-white placeholder:text-white/40" />
                      {countries.length > 1 && <button onClick={() => setCountries((cs) => cs.filter((x) => x.key !== c.key))} className="text-white/50 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input type="date" data-testid={`nt-arrive-${i}`} value={c.arrive_date} onChange={(e) => setC(c.key, "arrive_date", e.target.value)} className="rounded-xl h-10 bg-[#0A1B33]/50 border-white/20 text-white" />
                      <Input type="date" data-testid={`nt-depart-${i}`} value={c.depart_date} onChange={(e) => setC(c.key, "depart_date", e.target.value)} className="rounded-xl h-10 bg-[#0A1B33]/50 border-white/20 text-white" />
                    </div>
                  </div>
                ))}
                <Button data-testid="nt-add-country" onClick={() => setCountries((cs) => [...cs, emptyCountry()])} variant="outline" className="rounded-full w-full border-dashed border-white/30 text-white bg-transparent hover:bg-white/10"><Plus className="h-4 w-4 mr-1" /> Add another stop</Button>
              </>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3 text-sm">
            <div className="rounded-2xl p-4" style={{ background: "rgba(255,255,255,.06)" }}>
              <div className="font-heading font-bold text-xl text-[#E0A73A]">{trip.trip_name || "Untitled journey"}</div>
              <div className="text-white/75 mt-1">{[trip.start_date, trip.end_date].filter(Boolean).join(" → ") || "Dates TBD"} · Goal {trip.budget_goal ? `${trip.currency} ${Number(trip.budget_goal).toLocaleString()}` : "—"}</div>
              <div className="text-white/75 mt-1 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {copy.copy_countries ? `Route copied from ${fromName}` : validCountries.length ? validCountries.map((c) => c.name).join(" → ") : "No stops yet"}</div>
            </div>
            <ul className="text-white/80 space-y-1">
              <li>◌ Saved as <b>Planning</b> — hidden from partners</li>
              <li>{copy.copy_team ? "✓" : "–"} Team roster {copy.copy_team ? "carried over" : "starts empty"}</li>
              <li>{copy.copy_budget_items ? "✓" : "–"} Budget categories {copy.copy_budget_items ? "copied (amounts zero)" : "not copied"}</li>
              <li>{copy.copy_documents ? "✓" : "–"} Documents {copy.copy_documents ? "copied" : "not copied"}</li>
            </ul>
            <p className="text-xs text-white/50">{fromName} keeps running exactly as it is. Nothing is deleted or changed.</p>
          </div>
        )}

        <div className="flex gap-3 mt-6">
          {step > 1 && <Button variant="outline" data-testid="nt-prev" className="rounded-full flex-1 h-11 border-white/30 text-white bg-transparent hover:bg-white/10" onClick={() => setStep(step - 1)}>Back</Button>}
          {step < 3 ? (
            <Button data-testid="nt-next" className="rounded-full flex-1 h-11 bg-[#E0A73A] text-[#0A1B33] hover:bg-[#C9973A] font-semibold" onClick={() => { if (step === 1 && !trip.trip_name.trim()) return toast.error("Give the journey a name first."); setStep(step + 1); }}>Continue</Button>
          ) : (
            <Button data-testid="nt-create" disabled={saving} className="rounded-full flex-1 h-11 bg-[#E0A73A] text-[#0A1B33] hover:bg-[#C9973A] font-semibold" onClick={create}><Check className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save as Planning"}</Button>
          )}
        </div>
      </div>
    </div>
  );
}

function F({ label, testid, ...props }) {
  return (
    <div>
      <Label className="text-xs text-white/80">{label}</Label>
      <Input data-testid={testid} className="mt-1 rounded-xl h-11 bg-[#0A1B33]/50 border-white/20 text-white placeholder:text-white/40 focus-visible:ring-[#E0A73A]" {...props} />
    </div>
  );
}

function Toggle({ label, hint, checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-3 cursor-pointer">
      <span><span className="block">{label}</span><span className="block text-[11px] text-white/50">{hint}</span></span>
      <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} className="data-[state=checked]:bg-[#E0A73A]" />
    </label>
  );
}
